const PATH_SEGMENTS = /[^.[\]]+/g;

const toStringValue = (val) => {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map(toStringValue).filter(Boolean).join(', ');
  if (val instanceof Date) return val.toISOString();
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
};

const getByPath = (object, path) => {
  if (!path) return undefined;
  const matches = path.match(PATH_SEGMENTS) || [];
  return matches.reduce((acc, segment) => {
    if (acc == null) return undefined;
    return acc[segment];
  }, object);
};

const buildUrlFromTemplate = (template, params) =>
  template.replace(/\{(\w+)\}/g, (_, key) => encodeURIComponent(params[key] ?? ''));

const mapArtistCredit = (release) => {
  const credits = release['artist-credit'];
  if (!Array.isArray(credits)) return '';
  const names = credits
    .map((credit) => credit.name || credit.artist?.name || '')
    .filter(Boolean);
  return Array.from(new Set(names)).join(', ');
};

const mapLabelInfo = (release) => {
  const infos = release['label-info'];
  if (!Array.isArray(infos)) return '';
  return infos
    .map((info) => info.label?.name || '')
    .filter(Boolean)
    .join(', ');
};

const mapGenres = (release) => {
  const genres = release.genres;
  if (Array.isArray(genres) && genres.length) {
    return genres.map((g) => g.name || g).filter(Boolean).join(', ');
  }
  if (Array.isArray(release.tags) && release.tags.length) {
    return release.tags.map((t) => t.name || t).filter(Boolean).join(', ');
  }
  return '';
};

async function searchMusicBrainz(params) {
  const { query = '', artist = '', album = '', limit = 10 } = params || {};
  const parts = [];
  if (artist) parts.push(`artist:${artist}`);
  if (album) parts.push(`release:${album}`);
  if (query) parts.push(query);
  const queryString = parts.length ? parts.join(' AND ') : query || album || artist;
  if (!queryString) {
    return [];
  }
  const url = new URL('https://musicbrainz.org/ws/2/release/');
  url.searchParams.set('fmt', 'json');
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('query', queryString);

  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
    },
  });
  if (!response.ok) {
    const error = new Error(`MusicBrainz request failed with status ${response.status}`);
    error.status = response.status;
    throw error;
  }
  const data = await response.json();
  const releases = Array.isArray(data.releases) ? data.releases : [];
  return releases.map((release) => {
    const releaseDate = release.date || '';
    const year = releaseDate ? releaseDate.slice(0, 4) : '';
    const trackCount = typeof release['track-count'] === 'number'
      ? release['track-count']
      : Array.isArray(release.media)
        ? release.media.reduce((sum, medium) => sum + (medium['track-count'] || 0), 0)
        : undefined;

    return {
      id: `musicbrainz:${release.id}`,
      sourceId: 'musicbrainz',
      title: release.title || '',
      artist: mapArtistCredit(release),
      album: release.title || '',
      albumArtist: mapArtistCredit(release),
      year,
      releaseDate,
      country: release.country || '',
      label: mapLabelInfo(release),
      genre: mapGenres(release),
      trackCount,
      score: release.score ?? null,
      coverUrl: release.id ? `https://coverartarchive.org/release/${release.id}/front` : null,
      raw: release,
    };
  });
}

function parseDiscogsTitle(title) {
  if (!title) return { artist: '', album: '' };
  const firstDash = title.indexOf(' - ');
  if (firstDash === -1) return { artist: '', album: title };
  const artist = title.slice(0, firstDash).trim();
  const album = title.slice(firstDash + 3).trim();
  return { artist, album };
}

async function searchDiscogs(params) {
  const { query = '', artist = '', album = '', limit = 10, token = '' } = params || {};
  const url = new URL('https://api.discogs.com/database/search');
  url.searchParams.set('type', 'release');
  url.searchParams.set('per_page', String(limit));
  if (query) url.searchParams.set('q', query);
  if (artist) url.searchParams.set('artist', artist);
  if (album) url.searchParams.set('release_title', album);
  if (token) url.searchParams.set('token', token);

  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    const error = new Error(`Discogs request failed with status ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const data = await response.json();
  const results = Array.isArray(data.results) ? data.results : [];
  return results.map((item) => {
    const parsed = parseDiscogsTitle(item.title);
    const artistFromItem = typeof item.artist === 'string'
      ? item.artist
      : Array.isArray(item.artists)
        ? item.artists.map((a) => (typeof a === 'string' ? a : a?.name)).filter(Boolean).join(', ')
        : '';
    return {
      id: `discogs:${item.id}`,
      sourceId: 'discogs',
      title: parsed.album || item.title || '',
      artist: parsed.artist || artistFromItem,
      album: parsed.album || item.title || '',
      albumArtist: parsed.artist || '',
      year: item.year ? String(item.year) : '',
      genre: Array.isArray(item.genre) ? item.genre.join(', ') : '',
      style: Array.isArray(item.style) ? item.style.join(', ') : '',
      country: item.country || '',
      label: Array.isArray(item.label) ? item.label.join(', ') : '',
      coverUrl: item.cover_image || item.thumb || null,
      resourceUrl: item.resource_url || null,
      raw: item,
    };
  });
}

function normalizeCustomSourceResult(item, source) {
  const { fieldMap = {} } = source;
  const tags = {};
  if (fieldMap.tags && typeof fieldMap.tags === 'object') {
    for (const [key, path] of Object.entries(fieldMap.tags)) {
      const value = toStringValue(getByPath(item, path));
      if (value) tags[key] = value;
    }
  }

  const normalized = {
    id: `${source.id}:${Math.random().toString(36).slice(2)}`,
    sourceId: source.id,
    title: toStringValue(getByPath(item, fieldMap.title)),
    artist: toStringValue(getByPath(item, fieldMap.artist)),
    album: toStringValue(getByPath(item, fieldMap.album)),
    albumArtist: toStringValue(getByPath(item, fieldMap.albumArtist)),
    year: toStringValue(getByPath(item, fieldMap.year)),
    genre: toStringValue(getByPath(item, fieldMap.genre)),
    comment: toStringValue(getByPath(item, fieldMap.comment)),
    coverUrl: toStringValue(getByPath(item, fieldMap.coverUrl)),
    raw: item,
  };

  if (Object.keys(tags).length) {
    normalized.tagOverrides = tags;
  }

  return normalized;
}

async function searchCustomSource(source, params) {
  const { limit = 10 } = params || {};
  const url = buildUrlFromTemplate(source.urlTemplate, {
    query: params.query || '',
    artist: params.artist || '',
    album: params.album || '',
    track: params.track || '',
    limit,
  });

  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    const error = new Error(`Custom source request failed with status ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const data = await response.json();
  const resultPath = source.resultPath ? source.resultPath.match(PATH_SEGMENTS) : null;
  let collection = data;
  if (resultPath && resultPath.length) {
    collection = resultPath.reduce((acc, segment) => (acc == null ? undefined : acc[segment]), data);
  }
  if (!Array.isArray(collection)) {
    collection = collection ? [collection] : [];
  }
  return collection.map((item) => normalizeCustomSourceResult(item, source));
}

export const BUILT_IN_SOURCES = [
  {
    id: 'musicbrainz',
    name: 'MusicBrainz',
    description: 'Open music encyclopedia with rich metadata and cover art archive integration.',
    requiresAuth: false,
    canProvideCover: true,
    maxLimit: 25,
  },
  {
    id: 'discogs',
    name: 'Discogs',
    description: 'Discogs database search (provide personal token for best results).',
    requiresAuth: 'token',
    canProvideCover: true,
    maxLimit: 25,
  },
];

export async function searchTagSource(source, params, options = {}) {
  const limit = Math.min(options.limit ?? 10, 50);
  if (source === 'musicbrainz' || source?.id === 'musicbrainz') {
    return searchMusicBrainz({ ...params, limit });
  }
  if (source === 'discogs' || source?.id === 'discogs') {
    return searchDiscogs({ ...params, limit });
  }
  if (typeof source === 'object' && source.type === 'custom') {
    return searchCustomSource(source, { ...params, limit });
  }
  if (typeof source === 'string') {
    throw new Error(`Unknown tag source: ${source}`);
  }
  throw new Error('Unsupported tag source configuration');
}

export async function fetchCoverArtAsDataUrl(url) {
  if (!url) return null;
  const response = await fetch(url);
  if (!response.ok) {
    const error = new Error(`Cover fetch failed with status ${response.status}`);
    error.status = response.status;
    throw error;
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read cover blob'));
    reader.readAsDataURL(blob);
  });
}

export const CUSTOM_SOURCES_STORAGE_KEY = 'kemp3-custom-tag-sources';

export function normalizeCustomSourceInput(input) {
  return {
    id: input.id || `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    type: 'custom',
    name: input.name?.trim() || 'Custom Source',
    urlTemplate: input.urlTemplate?.trim() || '',
    resultPath: input.resultPath?.trim() || '',
    fieldMap: {
      title: input.fieldMap?.title || '',
      artist: input.fieldMap?.artist || '',
      album: input.fieldMap?.album || '',
      albumArtist: input.fieldMap?.albumArtist || '',
      year: input.fieldMap?.year || '',
      genre: input.fieldMap?.genre || '',
      comment: input.fieldMap?.comment || '',
      coverUrl: input.fieldMap?.coverUrl || '',
      tags: input.fieldMap?.tags || {},
    },
  };
}

export const defaultCustomFieldMap = {
  title: 'title',
  artist: 'artist',
  album: 'album',
  albumArtist: 'albumArtist',
  year: 'year',
  genre: 'genre',
  coverUrl: 'coverUrl',
};
